/**
 * TicketDetailPage — one support ticket with its full message thread.
 *
 * Route: `/support/:ticketId` (ProtectedRoute).
 *
 * Layout: messages render as left/right chat bubbles based on author
 * (user on the start side, admin on the end side — automatically
 * mirrored under RTL by CSS logical properties). A reply box at the
 * bottom posts a new message; if the ticket is `closed` the reply box
 * is hidden and a notice is shown instead.
 *
 * After a successful reply, useMutation invalidates the cached
 * ticket query so the new message appears immediately.
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Stack,
  Typography,
  TextField,
  Button,
  Box,
  IconButton,
  Skeleton,
  Alert,
  Chip,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import Container from '@/components/PageSheet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SendIcon from '@mui/icons-material/Send';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';

import LanguageToggle from '@/components/LanguageToggle';
import { supportApi } from '@/services/api';
import { selectLanguage, selectIsRtl } from '@/store/uiSlice';
import { formatDate } from '@/utils/date';

function statusChipColor(status) {
  switch (status) {
    case 'open':        return 'warning';
    case 'in_progress': return 'info';
    case 'resolved':    return 'success';
    case 'closed':      return 'default';
    default:            return 'default';
  }
}

function MessageBubble({ message, language, t }) {
  const isUser = message.author === 'user';
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="flex-end"
      // user messages start-side, admin messages end-side. Under RTL
      // these naturally mirror.
      sx={{
        justifyContent: isUser ? 'flex-end' : 'flex-start',
      }}
    >
      {!isUser && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            flexShrink: 0,
          }}
        >
          <SupportAgentIcon fontSize="small" />
        </Box>
      )}
      <Box
        sx={{
          maxWidth: '78%',
          bgcolor: isUser ? 'primary.main' : 'grey.100',
          color: isUser ? 'primary.contrastText' : 'text.primary',
          borderRadius: 2,
          // tail toward the speaker's side
          borderTopRightRadius: isUser ? 4 : 16,
          borderTopLeftRadius: isUser ? 16 : 4,
          px: 1.5,
          py: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            opacity: 0.85,
            display: 'block',
            mb: 0.25,
            fontWeight: 600,
          }}
        >
          {isUser ? t('support.detail.you') : t('support.detail.admin')}
        </Typography>
        <Typography
          variant="body2"
          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          {message.body}
        </Typography>
        <Typography
          variant="caption"
          sx={{ opacity: 0.7, display: 'block', mt: 0.5, fontSize: '0.7rem' }}
        >
          {formatDate(message.createdAt, language, { style: 'datetime' })}
        </Typography>
      </Box>
      {isUser && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: 'grey.300',
            color: 'text.primary',
            flexShrink: 0,
          }}
        >
          <PersonIcon fontSize="small" />
        </Box>
      )}
    </Stack>
  );
}

export function TicketDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const language = useSelector(selectLanguage);
  const isRtl = useSelector(selectIsRtl);
  const queryClient = useQueryClient();

  const [reply, setReply] = useState('');
  const [replyError, setReplyError] = useState(null);
  const bottomRef = useRef(null);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['support', 'tickets', ticketId],
    queryFn: () => supportApi.getTicket(ticketId),
    staleTime: 15 * 1000,
  });

  const ticket = data?.data?.ticket;

  const replyMutation = useMutation({
    mutationFn: () => supportApi.addMessage(ticketId, reply),
    onSuccess: ({ data }) => {
      // Replace the cache directly so the new message shows immediately
      // without waiting for a refetch.
      queryClient.setQueryData(['support', 'tickets', ticketId], { data });
      // Bump the list too — updatedAt changed.
      queryClient.invalidateQueries({ queryKey: ['support', 'tickets'] });
      setReply('');
    },
    onError: (err) => {
      const code = err?.response?.data?.error?.code;
      if (code === 'TICKET_CLOSED') {
        setReplyError(t('support.detail.ticketClosed'));
      } else if (code === 'VALIDATION_ERROR') {
        setReplyError(t('support.detail.replyRequired'));
      } else {
        setReplyError(t('support.error.generic'));
      }
    },
  });

  // Scroll to the latest message when the thread updates.
  useEffect(() => {
    if (ticket?.messages?.length) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [ticket?.messages?.length]);

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/support');
  };

  const handleSendReply = () => {
    setReplyError(null);
    if (!reply.trim()) {
      setReplyError(t('support.detail.replyRequired'));
      return;
    }
    replyMutation.mutate();
  };

  const canReply = ticket && ticket.status !== 'closed';

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <IconButton
              size="small"
              onClick={handleBack}
              aria-label={t('common.back')}
            >
              {isRtl ? <ArrowForwardIcon /> : <ArrowBackIcon />}
            </IconButton>
            <Typography variant="h6" component="h1" fontWeight={700} noWrap>
              {t('support.detail.title')}
            </Typography>
          </Stack>
          <LanguageToggle />
        </Stack>

        {/* Loading */}
        {isLoading && (
          <Stack spacing={2}>
            <Skeleton variant="text" width="70%" height={32} />
            <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
          </Stack>
        )}

        {/* Error */}
        {isError && (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => refetch()}
                disabled={isFetching}
              >
                {t('common.retry')}
              </Button>
            }
          >
            {t('support.error.loadFailed')}
          </Alert>
        )}

        {/* Ticket header card */}
        {!isLoading && !isError && ticket && (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {ticket.subject}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip
                  size="small"
                  color={statusChipColor(ticket.status)}
                  label={t(`support.status.${ticket.status}`, {
                    defaultValue: ticket.status,
                  })}
                  variant="filled"
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={t(`support.category.${ticket.category}`, {
                    defaultValue: ticket.category,
                  })}
                />
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Messages */}
        {!isLoading && !isError && ticket?.messages && (
          <Stack spacing={2}>
            {(ticket.messages || []).map((msg) => (
              <MessageBubble
                key={msg.id}
                message={msg}
                language={language}
                t={t}
              />
            ))}
            {/* sentinel for scroll-to-bottom */}
            <Box ref={bottomRef} />
          </Stack>
        )}

        {/* "Awaiting reply" hint when only the user has spoken so far */}
        {!isLoading && !isError && ticket?.status === 'open' && (
          <Alert severity="info" variant="outlined">
            {t('support.detail.awaitingReply')}
          </Alert>
        )}

        {/* Reply box — only when ticket is not closed */}
        {canReply && (
          <Stack spacing={1.5}>
            <TextField
              label={t('support.detail.replyPlaceholder')}
              value={reply}
              onChange={(e) => {
                setReply(e.target.value);
                if (replyError) setReplyError(null);
              }}
              disabled={replyMutation.isPending}
              fullWidth
              multiline
              rows={3}
              inputProps={{ maxLength: 2000 }}
              error={!!replyError}
              helperText={replyError ?? ' '}
            />
            <Button
              variant="contained"
              onClick={handleSendReply}
              disabled={replyMutation.isPending}
              startIcon={
                replyMutation.isPending ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
            >
              {replyMutation.isPending
                ? t('support.detail.sending')
                : t('support.detail.send')}
            </Button>
          </Stack>
        )}

        {/* Closed-ticket notice */}
        {!isLoading && !isError && ticket?.status === 'closed' && (
          <Alert severity="warning">
            {t('support.detail.ticketClosed')}
          </Alert>
        )}
      </Stack>
    </Container>
  );
}

export default TicketDetailPage;
